import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, Sparkles, Check } from 'lucide-react';
import { useConsoleTheme } from '../../lib/theme';

// SpotlightTour — a premium coachmark / product tour.
//
// Dims the page and "spotlights" a real element with an animated cutout that
// glides between targets, a pulsing glow ring, and a tooltip with a connector
// arrow pointing at the element. Steps walk the user through the actual UI.
// Centered steps (no target) render a focused intro/outro card.
//
// step: {
//   target?: string                              // CSS selector to spotlight
//   placement?: 'top'|'bottom'|'left'|'right'    // default 'bottom'
//   eyebrow?: string, title: string, body: string
//   finishLabel?: string                         // last step's primary button
// }
// Props: { steps, onClose, onFinish }.

const PAD = 8;
const TIP_W = 332;
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const SPRING = { type: 'spring', stiffness: 320, damping: 32, mass: 0.7 };

// Inline colours the light theme cannot reach through Tailwind
// (docs/light-mode.md §3–§4). Dark values are the originals. Light: the scrim is
// ink at 0.55 × the dark alpha (the generator's rule for black scrims), the
// glow ring uses the accent (non-text, ≥ 3:1). The step dots are styled in the
// portal section of theme-light.overrides.css through data-tour-dot.
const TOUR_COLORS = {
  dark: {
    spotScrim: '0 0 0 9999px rgba(4,4,7,0.78)',
    solidScrim: 'rgba(4,4,7,0.8)',
    glow: '0 0 0 2px #B0FC38, 0 0 26px 4px rgba(176,252,56,0.45)',
  },
  light: {
    spotScrim: '0 0 0 9999px rgba(28,27,22,0.43)',
    solidScrim: 'rgba(28,27,22,0.44)',
    glow: '0 0 0 2px #4D7C0F, 0 0 26px 4px rgba(77,124,15,0.35)',
  },
};

export default function SpotlightTour({ steps, onClose, onFinish }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState({ top: -9999, left: -9999, ready: false, arrow: null });
  const popRef = useRef(null);
  const { isLight } = useConsoleTheme();
  const colors = isLight ? TOUR_COLORS.light : TOUR_COLORS.dark;

  const step = steps[i] || null;
  const isLast = i === steps.length - 1;

  const finish = useCallback(() => (onFinish ? onFinish() : onClose?.()), [onFinish, onClose]);
  const next = useCallback(() => setI((x) => (x >= steps.length - 1 ? x : x + 1)), [steps.length]);
  const back = useCallback(() => setI((x) => Math.max(0, x - 1)), []);

  const measure = useCallback(() => {
    if (!step || !step.target) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
  }, [step]);

  // On each step: run its onEnter side-effect (e.g. trigger a capture), then
  // bring the — possibly newly-rendered — target into view and measure. Next is
  // disabled while an async onEnter is pending. Deps are [i] only on purpose:
  // the steps array is recreated each render, so depending on `step` would
  // re-fire onEnter on every render.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = steps[i];
      if (s?.onEnter) {
        try {
          const r = s.onEnter();
          if (r && typeof r.then === 'function') { setBusy(true); await r; }
        } catch (_e) { /* ignore */ }
      }
      if (cancelled) return;
      setBusy(false);
      const focus = () => {
        if (s?.target) {
          const el = document.querySelector(s.target);
          if (el?.scrollIntoView) el.scrollIntoView({ block: 'center', inline: 'nearest' });
        }
        measure();
      };
      requestAnimationFrame(focus);
      // Re-measure shortly after in case the target only just mounted.
      const t = setTimeout(focus, 160);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Stay aligned through scroll / resize.
  useEffect(() => {
    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [measure]);

  // Keyboard navigation — feels like a real product tour.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { if (!busy) { e.preventDefault(); isLast ? finish() : next(); } }
      else if (e.key === 'ArrowLeft') { if (!busy) { e.preventDefault(); back(); } }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLast, finish, next, back, onClose, busy]);

  // Position the tooltip + connector arrow from the target rect + tooltip size.
  useLayoutEffect(() => {
    const el = popRef.current;
    const pw = el?.offsetWidth || TIP_W;
    const ph = el?.offsetHeight || 180;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (!rect) {
      setPos({ top: clamp((vh - ph) / 2, 12, vh - ph - 12), left: clamp((vw - pw) / 2, 12, vw - pw - 12), ready: true, arrow: null });
      return;
    }
    const placement = step.placement || 'bottom';
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let top;
    let left;
    let edge;
    if (placement === 'top') { top = rect.top - ph - 14; left = cx - pw / 2; edge = 'bottom'; }
    else if (placement === 'left') { top = cy - ph / 2; left = rect.left - pw - 14; edge = 'right'; }
    else if (placement === 'right') { top = cy - ph / 2; left = rect.right + 14; edge = 'left'; }
    else { top = rect.bottom + 14; left = cx - pw / 2; edge = 'top'; }
    // Flip if it would overflow off-screen.
    if (placement === 'bottom' && top + ph > vh - 12) { top = rect.top - ph - 14; edge = 'bottom'; }
    if (placement === 'top' && top < 12) { top = rect.bottom + 14; edge = 'top'; }
    top = clamp(top, 12, vh - ph - 12);
    left = clamp(left, 12, vw - pw - 12);
    const arrow = (edge === 'top' || edge === 'bottom')
      ? { edge, offset: clamp(cx - left, 18, pw - 18) }
      : { edge, offset: clamp(cy - top, 18, ph - 18) };
    setPos({ top, left, ready: true, arrow });
  }, [rect, i, step]);

  if (!step) return null;

  const stepNo = i + 1;

  return (
    <motion.div className="fixed inset-0 z-[95]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
      {/* Dim + spotlight */}
      {rect ? (
        <motion.div
          className="fixed rounded-xl pointer-events-none"
          initial={false}
          animate={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
          transition={SPRING}
          style={{ boxShadow: colors.spotScrim }}
        >
          {/* pulsing glow ring */}
          <motion.div
            className="absolute -inset-px rounded-xl"
            animate={{ opacity: [0.45, 0.9, 0.45] }}
            transition={{ duration: 2.1, repeat: Infinity, ease: 'easeInOut' }}
            style={{ boxShadow: colors.glow }}
          />
        </motion.div>
      ) : (
        // Solid dim (no backdrop-filter — Chrome can't composite backdrop-blur
        // over a cross-origin iframe, which would let it show through bright).
        <div className="fixed inset-0" style={{ background: colors.solidScrim }} />
      )}

      {/* Tooltip */}
      <motion.div
        ref={popRef}
        initial={{ opacity: 0, scale: 0.95, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="fixed z-[96] w-[332px] max-w-[calc(100vw-24px)] rounded-2xl border border-white/[0.1] bg-gradient-to-b from-[#14161a] to-[#0b0b10] shadow-[0_24px_60px_-12px_rgba(0,0,0,0.8)] overflow-hidden"
        style={{ top: pos.top, left: pos.left, visibility: pos.ready ? 'visible' : 'hidden', transition: 'top 0.28s cubic-bezier(0.22,1,0.36,1), left 0.28s cubic-bezier(0.22,1,0.36,1)' }}
      >
        {/* gradient progress bar */}
        <div className="h-1 w-full bg-white/[0.06]">
          <motion.div
            className="h-full bg-gradient-to-r from-[#8FCC2E] to-[#B0FC38]"
            initial={false}
            animate={{ width: `${(stepNo / steps.length) * 100}%` }}
            transition={{ type: 'spring', stiffness: 200, damping: 26 }}
          />
        </div>

        {/* connector arrow */}
        {pos.arrow && <Arrow arrow={pos.arrow} />}

        <div className="p-4 pt-3.5">
          <div className="flex items-start justify-between gap-2">
            {step.eyebrow ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#B0FC38]/[0.12] border border-[#B0FC38]/30">
                <Sparkles className="w-3 h-3 text-[#B0FC38]" />
                <span className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase">{step.eyebrow}</span>
              </span>
            ) : (
              <span className="text-[10px] font-medium text-gray-600 tracking-wider uppercase">Step {stepNo} of {steps.length}</span>
            )}
            <button type="button" onClick={onClose} aria-label="Close tour" className="-mr-1 -mt-0.5 p-1 text-gray-500 hover:text-white transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* content animates in per step */}
          <motion.div key={i} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}>
            <h3 className="text-[15px] font-semibold text-white tracking-tight mt-2.5">{step.title}</h3>
            <p className="text-[12.5px] text-gray-400 leading-relaxed mt-1.5">{step.body}</p>
          </motion.div>

          <div className="flex items-center justify-between gap-3 mt-4">
            <div className="flex items-center gap-1.5">
              {steps.map((s, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setI(idx)}
                  aria-label={`Go to step ${idx + 1}`}
                  data-tour-dot={idx === i ? 'current' : idx < i ? 'done' : 'todo'}
                  className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? 'w-5 bg-[#B0FC38]' : idx < i ? 'w-1.5 bg-[#B0FC38]/50' : 'w-1.5 bg-white/15 hover:bg-white/30'}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1">
              {i > 0 ? (
                <button type="button" onClick={back} className="px-2.5 py-1.5 text-[12px] text-gray-400 hover:text-white inline-flex items-center gap-1 transition-colors">
                  <ArrowLeft className="w-3 h-3" /> Back
                </button>
              ) : (
                <button type="button" onClick={onClose} className="px-2.5 py-1.5 text-[12px] text-gray-500 hover:text-white transition-colors">Skip</button>
              )}
              <button
                type="button"
                onClick={isLast ? finish : next}
                disabled={busy}
                className="group px-4 py-1.5 rounded-full bg-[#B0FC38] text-gray-900 text-[12px] font-semibold inline-flex items-center gap-1.5 shadow-lg shadow-[#B0FC38]/25 hover:shadow-[#B0FC38]/40 hover:bg-[#9BE22E] transition-all disabled:opacity-60 disabled:cursor-wait"
              >
                {busy ? (
                  <span className="w-3 h-3 border-2 border-gray-900/70 border-t-transparent rounded-full animate-spin" />
                ) : isLast ? (
                  <>{step.finishLabel || 'Done'} {step.finishLabel ? <ArrowRight className="w-3 h-3" /> : <Check className="w-3 h-3" />}</>
                ) : (
                  <>Next <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Small diamond pointing from the tooltip toward the spotlighted element.
function Arrow({ arrow }) {
  const base = 'absolute w-2.5 h-2.5 bg-[#14161a] border-white/[0.1] rotate-45';
  const style = {};
  if (arrow.edge === 'top') Object.assign(style, { top: -5, left: arrow.offset, marginLeft: -5 });
  if (arrow.edge === 'bottom') Object.assign(style, { bottom: -5, left: arrow.offset, marginLeft: -5 });
  if (arrow.edge === 'left') Object.assign(style, { left: -5, top: arrow.offset, marginTop: -5 });
  if (arrow.edge === 'right') Object.assign(style, { right: -5, top: arrow.offset, marginTop: -5 });
  const borders = {
    top: 'border-t border-l',
    bottom: 'border-b border-r',
    left: 'border-b border-l',
    right: 'border-t border-r',
  }[arrow.edge];
  return <div className={`${base} ${borders}`} style={style} />;
}
